import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import {
  Settings, Key, Video, Palette, Info, CheckCircle2,
  Moon, Sun, Mail, Loader2, Send, AlertTriangle, Shield,
  Globe, Eye, EyeOff, HelpCircle, ExternalLink, Presentation,
  Download, FileText, Tag, Trash2, Plus
} from 'lucide-react';
import { saveEmailConfig, getEmailConfig, testEmailConfig, getPresentations, downloadPresentation, getTags, createTag, deleteTag as deleteTagApi } from '../services/api';
import { useRegion } from '../context/RegionContext';
import { ApiKeyManager, ApiDocumentation, ApiUsageDashboard } from '../components/ApiIntegration';

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { region } = useRegion();
  const [interviewDefaults, setInterviewDefaults] = useState({
    questionCount: '8',
    timePerQuestion: '120',
    language: 'en-US'
  });

  // Presentations state
  const [presentations, setPresentations] = useState([]);
  const [loadingPresentations, setLoadingPresentations] = useState(false);

  // Tags state
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [creatingTag, setCreatingTag] = useState(false);

  // Email config state
  const [emailRegion, setEmailRegion] = useState('global');
  const [connectionType, setConnectionType] = useState('exchange');
  const [emailConfig, setEmailConfig] = useState({
    smtp_server: 'smtp.office365.com',
    smtp_port: 587,
    email: '',
    password: '',
    sender_name: 'HireFast AI',
    tenant_id: '',
    client_id: '',
    client_secret: '',
  });
  const [existingConfig, setExistingConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const handleSaveDefaults = () => {
    localStorage.setItem('interview_defaults', JSON.stringify(interviewDefaults));
    toast.success('Interview defaults saved');
  };

  // Load presentations
  useEffect(() => {
    const loadPresentations = async () => {
      setLoadingPresentations(true);
      try {
        const data = await getPresentations();
        setPresentations(data.presentations || []);
      } catch (e) { console.error('Failed to load presentations:', e); }
      finally { setLoadingPresentations(false); }
    };
    loadPresentations();
  }, []);

  // Load tags
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTags();
  }, [region]);

  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const data = await getTags(region);
      setTags(data.tags || []);
    } catch (e) { console.error('Failed to load tags:', e); }
    finally { setLoadingTags(false); }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      setCreatingTag(true);
      const tag = await createTag(newTagName.trim(), newTagColor, region);
      setTags(prev => [...prev, tag]);
      setNewTagName('');
      toast.success(`Tag "${tag.name}" created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleDeleteTag = async (tagId, tagName) => {
    try {
      await deleteTagApi(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
      toast.success(`Tag "${tagName}" deleted`);
    } catch (e) {
      toast.error('Failed to delete tag');
    }
  };

  // Load email config when region changes
  useEffect(() => {
    const loadConfig = async () => {
      setLoadingConfig(true);
      try {
        const config = await getEmailConfig(emailRegion);
        setExistingConfig(config);
        if (config.configured) {
          setConnectionType(config.connection_type || 'exchange');
          setEmailConfig({
            smtp_server: config.smtp_server || 'smtp.office365.com',
            smtp_port: config.smtp_port || 587,
            email: config.email || '',
            password: '',
            sender_name: config.sender_name || 'HireFast AI',
            tenant_id: config.tenant_id || '',
            client_id: config.client_id || '',
            client_secret: '',
          });
          setTestRecipient(config.email || '');
        }
      } catch (e) {
        console.error('Failed to load email config:', e);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, [emailRegion]);

  useEffect(() => {
    const saved = localStorage.getItem('interview_defaults');
    if (saved) {
      try { setInterviewDefaults(JSON.parse(saved)); } catch (e) { console.error('Failed to parse interview defaults:', e); }
    }
  }, []);

  const handleSaveEmailConfig = async () => {
    if (!emailConfig.email) {
      toast.error('Email address is required');
      return;
    }
    if (connectionType === 'exchange') {
      if (!emailConfig.tenant_id || !emailConfig.client_id || !emailConfig.client_secret) {
        // Check if existing config already has secrets saved
        if (!existingConfig?.tenant_id || !existingConfig?.client_id) {
          toast.error('Tenant ID, Client ID, and Client Secret are required for Exchange Online');
          return;
        }
      }
    } else {
      if (!emailConfig.password && !existingConfig?.configured) {
        toast.error('Password is required for SMTP');
        return;
      }
    }

    setSavingConfig(true);
    try {
      const payload = {
        connection_type: connectionType,
        email: emailConfig.email,
        sender_name: emailConfig.sender_name,
        region: emailRegion,
      };
      if (connectionType === 'smtp') {
        payload.smtp_server = emailConfig.smtp_server;
        payload.smtp_port = emailConfig.smtp_port;
        if (emailConfig.password) payload.password = emailConfig.password;
      } else {
        if (emailConfig.tenant_id) payload.tenant_id = emailConfig.tenant_id;
        if (emailConfig.client_id) payload.client_id = emailConfig.client_id;
        if (emailConfig.client_secret) payload.client_secret = emailConfig.client_secret;
      }
      await saveEmailConfig(payload);
      toast.success(`Email configuration saved for ${emailRegion === 'global' ? 'Global' : emailRegion}`);
      const config = await getEmailConfig(emailRegion);
      setExistingConfig(config);
    } catch (e) {
      toast.error('Failed to save email configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestEmail = async () => {
    if (!emailConfig.email) {
      toast.error('Enter email address first');
      return;
    }
    if (!testRecipient) {
      toast.error('Enter a test recipient email');
      return;
    }
    setTestingEmail(true);
    try {
      const payload = {
        connection_type: connectionType,
        email: emailConfig.email,
        test_recipient: testRecipient,
      };
      if (connectionType === 'smtp') {
        payload.smtp_server = emailConfig.smtp_server;
        payload.smtp_port = emailConfig.smtp_port;
        payload.password = emailConfig.password;
      } else {
        payload.tenant_id = emailConfig.tenant_id;
        payload.client_id = emailConfig.client_id;
        payload.client_secret = emailConfig.client_secret;
      }
      await testEmailConfig(payload);
      toast.success('Test email sent! Check inbox of ' + testRecipient);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed. Check your credentials.');
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure email integration and application preferences
        </p>
      </div>

      {/* Email Configuration */}
      <Card data-testid="settings-email-config" className="border-2 border-primary/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Mail className="h-4 w-4 text-blue-500" strokeWidth={1.8} />
              Email Configuration
            </CardTitle>
            <div className="flex items-center gap-2">
              {existingConfig?.configured && (
                <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {existingConfig?.connection_type === 'exchange' ? 'Exchange Online' : 'SMTP'} Configured
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Region scope selector */}
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5" /> Configuration Scope
            </Label>
            <Tabs value={emailRegion} onValueChange={setEmailRegion}>
              <TabsList className="w-full grid grid-cols-4">
                <TabsTrigger value="global" data-testid="email-scope-global">Global</TabsTrigger>
                <TabsTrigger value="NA" data-testid="email-scope-na">North America</TabsTrigger>
                <TabsTrigger value="UK_EMEA" data-testid="email-scope-uk">UK & EMEA</TabsTrigger>
                <TabsTrigger value="APAC" data-testid="email-scope-apac">APAC</TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-[10px] text-muted-foreground">
              {emailRegion === 'global'
                ? 'Global config is used as fallback for all regions without specific configuration.'
                : `Config for ${emailRegion} region. Falls back to Global if not set.`}
            </p>
          </div>

          {loadingConfig ? (
            <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading configuration...
            </div>
          ) : (
            <>
              {/* Connection Type Toggle */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Connection Type</Label>
                <Tabs value={connectionType} onValueChange={setConnectionType}>
                  <TabsList>
                    <TabsTrigger value="exchange" data-testid="conn-type-exchange">
                      Exchange Online (OAuth2)
                    </TabsTrigger>
                    <TabsTrigger value="smtp" data-testid="conn-type-smtp">
                      SMTP (Basic Auth)
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {connectionType === 'exchange' ? (
                /* ===== EXCHANGE ONLINE CONFIG ===== */
                <div className="space-y-4">
                  <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                      <div className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                        <p className="font-medium">Exchange Online requires Azure AD App Registration</p>
                        <p>You need a Tenant ID, Client ID, and Client Secret from your Azure AD portal.</p>
                        <button
                          className="text-blue-600 underline text-xs font-medium"
                          onClick={() => setShowSetupGuide(!showSetupGuide)}
                        >
                          {showSetupGuide ? 'Hide setup guide' : 'Show step-by-step setup guide'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Setup guide */}
                  {showSetupGuide && (
                    <div className="p-4 bg-muted/50 border rounded-lg text-xs space-y-3" data-testid="exchange-setup-guide">
                      <h4 className="font-semibold text-sm">Azure AD App Setup Guide</h4>
                      <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                        <li>Go to <a href="https://entra.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">entra.microsoft.com</a> (Azure AD Portal)</li>
                        <li>Navigate to <strong>App registrations</strong> → <strong>New registration</strong></li>
                        <li>Name it "HireFast AI Email" → Register</li>
                        <li>Copy the <strong>Application (client) ID</strong> — this is your <strong>Client ID</strong></li>
                        <li>Copy the <strong>Directory (tenant) ID</strong> — this is your <strong>Tenant ID</strong></li>
                        <li>Go to <strong>Certificates & secrets</strong> → <strong>New client secret</strong> → Copy the <strong>Value</strong> — this is your <strong>Client Secret</strong></li>
                        <li>Go to <strong>API permissions</strong> → <strong>Add a permission</strong></li>
                        <li>Select <strong>Microsoft Graph</strong> → <strong>Application permissions</strong></li>
                        <li>Search and check <strong>Mail.Send</strong></li>
                        <li>Click <strong>Grant admin consent</strong> for your organization</li>
                      </ol>
                      <p className="text-muted-foreground mt-2">After completing these steps, enter the values below and click "Send Test" to verify.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Sender Email Address *</Label>
                      <Input
                        type="email"
                        value={emailConfig.email}
                        onChange={(e) => setEmailConfig(p => ({ ...p, email: e.target.value }))}
                        placeholder="noreply@yourcompany.com"
                        data-testid="email-address-input"
                      />
                      {existingConfig?.configured && existingConfig?.email && (
                        <p className="text-[10px] text-muted-foreground">Current: {existingConfig.email}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Sender Display Name</Label>
                      <Input
                        value={emailConfig.sender_name}
                        onChange={(e) => setEmailConfig(p => ({ ...p, sender_name: e.target.value }))}
                        placeholder="HireFast AI"
                        data-testid="email-sender-name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Tenant ID *</Label>
                      <Input
                        value={emailConfig.tenant_id}
                        onChange={(e) => setEmailConfig(p => ({ ...p, tenant_id: e.target.value }))}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        data-testid="email-tenant-id"
                      />
                      {existingConfig?.configured && existingConfig?.tenant_id && (
                        <p className="text-[10px] text-muted-foreground">Saved: {existingConfig.tenant_id}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Client ID (Application ID) *</Label>
                      <Input
                        value={emailConfig.client_id}
                        onChange={(e) => setEmailConfig(p => ({ ...p, client_id: e.target.value }))}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        data-testid="email-client-id"
                      />
                      {existingConfig?.configured && existingConfig?.client_id && (
                        <p className="text-[10px] text-muted-foreground">Saved: {existingConfig.client_id}</p>
                      )}
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label className="text-xs">Client Secret *</Label>
                      <div className="relative">
                        <Input
                          type={showSecret ? 'text' : 'password'}
                          value={emailConfig.client_secret}
                          onChange={(e) => setEmailConfig(p => ({ ...p, client_secret: e.target.value }))}
                          placeholder={existingConfig?.configured && existingConfig?.client_secret_masked ? '••••••••  (enter to update)' : 'Enter client secret value'}
                          data-testid="email-client-secret"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                          onClick={() => setShowSecret(!showSecret)}
                        >
                          {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                      {existingConfig?.configured && existingConfig?.client_secret_masked && (
                        <p className="text-[10px] text-muted-foreground">Saved: {existingConfig.client_secret_masked}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* ===== SMTP CONFIG ===== */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">SMTP Server</Label>
                    <Input
                      value={emailConfig.smtp_server}
                      onChange={(e) => setEmailConfig(p => ({ ...p, smtp_server: e.target.value }))}
                      placeholder="smtp.office365.com"
                      data-testid="email-smtp-server"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Port</Label>
                    <Input
                      type="number"
                      value={emailConfig.smtp_port}
                      onChange={(e) => setEmailConfig(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))}
                      placeholder="587"
                      data-testid="email-smtp-port"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email Address</Label>
                    <Input
                      type="email"
                      value={emailConfig.email}
                      onChange={(e) => setEmailConfig(p => ({ ...p, email: e.target.value }))}
                      placeholder="yourname@company.com"
                      data-testid="email-address-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={emailConfig.password}
                        onChange={(e) => setEmailConfig(p => ({ ...p, password: e.target.value }))}
                        placeholder={existingConfig?.configured ? '••••••••  (enter to update)' : 'Enter password'}
                        data-testid="email-password-input"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    {existingConfig?.configured && existingConfig?.password_masked && (
                      <p className="text-[10px] text-muted-foreground">Saved: {existingConfig.password_masked}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Sender Display Name</Label>
                    <Input
                      value={emailConfig.sender_name}
                      onChange={(e) => setEmailConfig(p => ({ ...p, sender_name: e.target.value }))}
                      placeholder="HireFast AI"
                      data-testid="email-sender-name"
                    />
                  </div>
                </div>
              )}

              {/* Save Button */}
              <Button
                onClick={handleSaveEmailConfig}
                disabled={savingConfig || !emailConfig.email}
                className="w-full"
                data-testid="save-email-config-btn"
              >
                {savingConfig ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                ) : (
                  <><Shield className="h-4 w-4 mr-2" />Save Email Configuration</>
                )}
              </Button>

              <Separator />

              {/* Test Email */}
              <div className="space-y-3">
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  <Send className="h-3.5 w-3.5" /> Test Email Connection
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder="Send test email to..."
                    className="flex-1"
                    data-testid="test-recipient-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={testingEmail || !emailConfig.email || !testRecipient}
                    data-testid="test-email-btn"
                  >
                    {testingEmail ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" />Testing...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" />Send Test</>
                    )}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  A test email will be sent to verify your {connectionType === 'exchange' ? 'Exchange Online' : 'SMTP'} credentials are working.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Appearance */}
        <Card data-testid="settings-appearance">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Palette className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Theme</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'light', label: 'Light', icon: Sun },
                  { value: 'dark', label: 'Dark', icon: Moon },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      theme === opt.value
                        ? 'border-indigo-500 bg-indigo-500/5'
                        : 'border-border hover:border-indigo-500/30'
                    }`}
                    data-testid={`theme-${opt.value}-btn`}
                  >
                    <opt.icon className={`h-5 w-5 ${theme === opt.value ? 'text-indigo-500' : 'text-muted-foreground'}`} />
                    <span className={`text-xs font-medium ${theme === opt.value ? 'text-indigo-500' : 'text-muted-foreground'}`}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interview Defaults */}
        <Card data-testid="settings-interview-defaults">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Video className="h-4 w-4 text-cyan-500" strokeWidth={1.8} />
              Interview Defaults
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="questionCount">Default Questions Per Interview</Label>
              <Select value={interviewDefaults.questionCount} onValueChange={(v) => setInterviewDefaults(p => ({ ...p, questionCount: v }))}>
                <SelectTrigger id="questionCount"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['5', '6', '7', '8', '10', '12'].map(n => (
                    <SelectItem key={n} value={n}>{n} questions</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timePerQuestion">Time Per Question (seconds)</Label>
              <Select value={interviewDefaults.timePerQuestion} onValueChange={(v) => setInterviewDefaults(p => ({ ...p, timePerQuestion: v }))}>
                <SelectTrigger id="timePerQuestion"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['60', '90', '120', '150', '180'].map(n => (
                    <SelectItem key={n} value={n}>{n}s ({Math.round(n / 60)} min)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSaveDefaults} className="w-full" data-testid="save-interview-defaults-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Save Defaults
            </Button>
          </CardContent>
        </Card>

        {/* API Configuration */}
        <Card data-testid="settings-api-config">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Key className="h-4 w-4 text-amber-500" strokeWidth={1.8} />
              API Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg border space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium">LLM API Connected</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Lemonfox AI is configured and active.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tag Management */}
        <Card data-testid="settings-tags">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Tag className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
              Tag Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">Create and manage tags for organizing candidates across jobs.</p>

            {/* Create new tag */}
            <div className="flex items-center gap-2">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="New tag name..."
                className="flex-1 h-9"
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
                data-testid="settings-new-tag-input"
              />
              <div className="flex gap-1">
                {['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#ef4444', '#22c55e'].map(color => (
                  <button
                    key={color}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${newTagColor === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewTagColor(color)}
                  />
                ))}
              </div>
              <Button size="sm" onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()} data-testid="settings-create-tag-btn">
                {creatingTag ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>

            {/* Tag list */}
            {loadingTags ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading tags...</div>
            ) : tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <div
                    key={tag.id}
                    className="group flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium"
                    style={{ backgroundColor: `${tag.color}15`, color: tag.color, borderColor: `${tag.color}40` }}
                    data-testid={`settings-tag-${tag.id}`}
                  >
                    {tag.name}
                    <button
                      onClick={() => handleDeleteTag(tag.id, tag.name)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                      data-testid={`delete-settings-tag-${tag.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No tags created yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Presentations */}
        <Card data-testid="settings-presentations">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileText className="h-4 w-4 text-violet-500" strokeWidth={1.8} />
              Presentations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingPresentations ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
            ) : presentations.length > 0 ? (
              <div className="space-y-2">
                {presentations.map((ppt) => (
                  <div key={ppt.filename} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-lg bg-violet-500/10">
                        <FileText className="h-4 w-4 text-violet-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{ppt.display_name}</p>
                        <p className="text-xs text-muted-foreground">{ppt.size_kb} KB</p>
                      </div>
                    </div>
                    <a href={downloadPresentation(ppt.filename)} download data-testid={`download-ppt-${ppt.filename}`}>
                      <Button variant="outline" size="sm">
                        <Download className="h-3.5 w-3.5 mr-1" /> Download
                      </Button>
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No presentations available</p>
                <p className="text-xs">PPT files placed in the presentations folder will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* About */}
        <Card data-testid="settings-about">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Info className="h-4 w-4 text-blue-500" strokeWidth={1.8} />
              About
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-indigo-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>H</span>
              </div>
              <div>
                <h3 className="font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>HireFast AI</h3>
                <p className="text-xs text-muted-foreground">AI-Powered Recruitment Intelligence</p>
              </div>
            </div>
            <Separator />
            <div className="space-y-1.5 text-xs text-muted-foreground">
              <p>Version 2.3.0</p>
              <p>Powered by Lemonfox AI + Exchange Online</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* API Integration */}
      <Card data-testid="settings-api-integration" className="border-2 border-primary/10">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Key className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
            API Integration (Ceipal)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="keys">
            <TabsList className="mb-4">
              <TabsTrigger value="keys" data-testid="api-tab-keys">API Keys</TabsTrigger>
              <TabsTrigger value="docs" data-testid="api-tab-docs">API Documentation</TabsTrigger>
              <TabsTrigger value="usage" data-testid="api-tab-usage">Usage Analytics</TabsTrigger>
            </TabsList>
            <TabsContent value="keys"><ApiKeyManager /></TabsContent>
            <TabsContent value="docs"><ApiDocumentation /></TabsContent>
            <TabsContent value="usage"><ApiUsageDashboard /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
